/**
 * New-patient throttling presets (QMD-4170).
 *
 * The admin app is used by VAs and other non-engineering staff, so the provider-level control
 * ships as a named preset rather than the raw numbers it maps onto — see QMD-4170's 2026-08-31
 * scope change. `MaxConsecutiveNewPatientVisits` must never be editable or displayed as a number
 * anywhere in admin; this module is the only place the preset/number correspondence exists.
 *
 * The presets are a client-side abstraction. The backend has no matching enum — it reads the two
 * raw fields on `DoctorProductivityOverrideAdminApiContract`, so no backend change is required.
 */

/** A preset a VA can pick. `default` means "no override — take the system default". */
export type ThrottlingPreset = 'default' | 'off' | 'low' | 'medium' | 'high'

/**
 * What a stored override resolves to for display. `custom` covers a `MaxConsecutiveNewPatientVisits`
 * that no preset maps onto — only reachable by writing the column outside admin. It is shown as a
 * disabled option so the value is neither exposed as a number nor silently overwritten by a save.
 */
export type ThrottlingPresetOrCustom = ThrottlingPreset | 'custom'

/** Offered in escalating order of restriction. */
export const THROTTLING_PRESET_OPTIONS: readonly ThrottlingPreset[] = ['default', 'off', 'low', 'medium', 'high']

/**
 * Mirrors `appsettings.Common.json` → `NewPatientVisitSpacing.ExemptDurationMinutes`. Providers
 * whose appointment length is at or above this are exempt from spacing regardless of their preset.
 * Hardcoded for the same reason as above.
 */
export const SPACING_EXEMPT_DURATION_MINUTES = 20

export const THROTTLING_PRESET_LABELS: Record<ThrottlingPresetOrCustom, string> = {
  // Deliberately does not name the value it currently resolves to. The backend global
  // (`appsettings.Common.json` → `NewPatientVisitSpacing.MaxConsecutive`) is not exposed by any
  // endpoint, so naming it here would be an unverifiable claim that goes stale the moment it
  // changes — and it would read as a duplicate of the 'Medium' option one row below, which is a
  // different thing: 'Medium' pins the provider to 2, this one tracks whatever the global is.
  default: 'System default',
  off: 'Off',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  custom: 'Custom',
}

/**
 * Helper text for the select. Gives a **directional** sense only — deliberately never states how
 * many appointments a preset actually allows.
 *
 * Naming the count would undo the point of the preset: QMD-4170's scope change exists so the
 * literal spacing rules do not circulate to VAs and, through them, to providers. "At most 3 new
 * patients in a row" surfaces `MaxConsecutiveNewPatientVisits` just as plainly as an input would,
 * and it also pins copy to a value that is only a backend config edit away from being wrong.
 */
export const THROTTLING_PRESET_DESCRIPTIONS: Record<ThrottlingPresetOrCustom, string> = {
  default: 'Takes the system default.',
  off: 'No limit on new patient appointments in a row.',
  low: 'Allows more new patient appointments in a row.',
  medium: 'Allows a moderate number of new patient appointments in a row.',
  high: 'Allows fewer new patient appointments in a row.',
  custom: 'Set outside of admin.',
}

/** The stored shape, as it arrives on `DoctorProductivityOverrideAdminApiContract`. */
export type StoredThrottling = {
  isExempt: boolean | undefined
  maxConsecutive: number | null | undefined
}

const PRESET_BY_MAX_CONSECUTIVE = new Map<number, ThrottlingPreset>([
  [3, 'low'],
  [2, 'medium'],
  [1, 'high'],
])

const MAX_CONSECUTIVE_BY_PRESET: Record<ThrottlingPreset, number | null> = {
  default: null,
  off: null,
  low: 3,
  medium: 2,
  high: 1,
}

/** Convert a picked preset into the two fields the productivity-override endpoint accepts. */
export function fromThrottlingPreset(preset: ThrottlingPreset): {
  isExemptFromNewPatientSpacing: boolean
  maxConsecutiveNewPatientVisits: number | null
} {
  return {
    isExemptFromNewPatientSpacing: preset === 'off',
    maxConsecutiveNewPatientVisits: MAX_CONSECUTIVE_BY_PRESET[preset],
  }
}

/**
 * Convert a stored override back into the preset to display.
 *
 * An exemption alongside a stored max-consecutive value is a combination no preset writes, so it
 * reads as `custom` rather than `off`. Collapsing it to `off` would be lossy: `off` writes
 * `maxConsecutive: null`, so a later duration-only edit would silently destroy a number that was
 * set out of band, and turning the exemption off afterwards would drop the provider to the global
 * default instead of the value someone stored for them.
 */
export function toThrottlingPreset({ isExempt, maxConsecutive }: StoredThrottling): ThrottlingPresetOrCustom {
  const hasStoredMax = maxConsecutive !== null && maxConsecutive !== undefined
  if (isExempt === true) return hasStoredMax ? 'custom' : 'off'
  if (!hasStoredMax) return 'default'
  return PRESET_BY_MAX_CONSECUTIVE.get(maxConsecutive) ?? 'custom'
}

/**
 * Whether the appointment length alone exempts this provider, making the preset inert. `null`
 * duration (no value known) is treated as in-scope so the control is never wrongly dismissed.
 */
export function isThrottlingMootForDuration(durationMinutes: number | null): boolean {
  if (durationMinutes === null) return false
  return durationMinutes >= SPACING_EXEMPT_DURATION_MINUTES
}
